// Words for the sync indicator (pure, so it is unit-testable). One line that answers "is my work saved?", in the order
// that matters to the person at the keyboard: refused edits first, then being offline, then a failing sync, then edits
// on their way, then downloading, then all good ("Live" while the server's change signal is connected, else "Up to
// date"). Mirrors the iOS Team Sync status title (SyncCenter.statusTitle), where `.offline` and `.error` win over the
// pending-uploads count.

import type { SyncStatus } from '@/sync/status';

export type SyncTone = 'ok' | 'busy' | 'offline' | 'warning' | 'danger';

export interface SyncLabel {
  label: string;
  tone: SyncTone;
  /** Longer explanation for the tooltip / screen readers. */
  detail: string | null;
  /** Work is in progress (spins the refresh icon). */
  busy: boolean;
}

function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

export function describeSync(status: SyncStatus): SyncLabel {
  const failed = Math.max(0, status.failed);
  // `pending` counts every outbox entry, failed ones included.
  const waiting = Math.max(0, status.pending - failed);

  if (failed > 0) {
    return {
      label: `Not saved ${failed.toLocaleString()}`,
      tone: 'danger',
      detail: `The server refused ${plural(failed, 'change', 'changes')}. Open the item to review it.`,
      busy: false,
    };
  }
  if (status.phase === 'offline') {
    return {
      label: 'Offline',
      tone: 'offline',
      detail:
        waiting > 0
          ? `${plural(waiting, 'change', 'changes')} will save when the connection is back.`
          : 'Showing the last synced data. Changes save when the connection is back.',
      busy: false,
    };
  }
  // Before the pending count: after a server error the engine stays in `error` with the edits still queued (it retries
  // them on the next poll), and a spinning "Saving N" would hide that nothing is getting through.
  if (status.phase === 'error') {
    const reason = status.error ? `Couldn't sync: ${status.error}` : "Couldn't sync.";
    return {
      label: 'Sync problem',
      tone: 'warning',
      detail:
        waiting > 0
          ? `${reason} ${plural(waiting, 'change is', 'changes are')} waiting to save. Try Refresh.`
          : `${reason} Try Refresh.`,
      busy: false,
    };
  }
  if (waiting > 0) {
    return {
      label: `Saving ${waiting.toLocaleString()}`,
      tone: 'busy',
      detail: `Saving ${plural(waiting, 'change', 'changes')}…`,
      busy: true,
    };
  }
  if (status.phase === 'starting' || status.phase === 'pulling' || status.fullResync) {
    const applied = status.progress?.applied ?? 0;
    return {
      label: 'Syncing…',
      tone: 'busy',
      detail: status.fullResync
        ? applied > 0
          ? `Loading your company data: ${plural(applied, 'item', 'items')} so far.`
          : 'Loading your company data…'
        : 'Checking for changes from your team…',
      busy: true,
    };
  }
  if (status.phase === 'pushing') {
    return { label: 'Saving…', tone: 'busy', detail: 'Saving your changes…', busy: true };
  }
  if (status.live) {
    return { label: 'Live', tone: 'ok', detail: 'Changes from your team show up here as they sync.', busy: false };
  }
  return { label: 'Up to date', tone: 'ok', detail: null, busy: false };
}
